import Color from 'color';
import { defineMessages, IntlShape } from 'react-intl';

import {
  WorkDay,
  WorkHour,
  WorkTableData,
} from '../../../../models/work-table/WorkTableData';

import WorkDaysCell from './days/WorkDaysCell';
import EditableCell from './hours/EditableCell';

const messages = defineMessages({
  workDays: {
    id: 'core.date.weekDays',
    defaultMessage: 'Monday_Tuesday_Wednesday_Thursday_Friday_Saturday_Sunday',
  },
  totalBase: {
    id: 'core.worktable.hoursTotal.base',
    defaultMessage: 'Total of Base Hours',
  },
  hourNumber: {
    id: 'core.worktable.hours.number',
    defaultMessage: 'No',
  },
  hourType: {
    id: 'core.worktable.hours.type',
    defaultMessage: 'Hour Type',
  },
  totalPresence: {
    id: 'core.worktable.hoursTotal.presence',
    defaultMessage: 'Total for the Days of Presence',
  },
  totalAbsence: {
    id: 'core.worktable.hoursTotal.absence',
    defaultMessage: 'Total for the Days of Absence',
  },
  totalAll: {
    id: 'core.worktable.hoursTotal.summary',
    defaultMessage: 'Total',
  },
});

const factHoursDays = (
  workHours: Record<string, WorkHour>,
  workDays: WorkDay[]
) => {
  let totalHours = 0;
  const days = [];
  for (const hourType of Object.values(workHours)) {
    if (hourType.isBase) {
      for (const day of hourType.worksByDay) {
        if (!workDays[day.day - 1].isAbsence && day.hours) {
          days[day.day - 1] = true;
          totalHours += day.hours;
        }
      }
    }
  }
  return { totalHours, totalDays: days.filter(Boolean).length };
};

export function mapHoursTable(state: WorkTableData, intl: IntlShape) {
  const workDays = state.workDays.reduce(
    (res, d) => {
      const { day, ...rest } = d;
      res[day] = rest;
      return res;
    },
    {} as Record<number, Omit<WorkDay, 'day'>>
  );

  // max day in current month
  const maxDay = new Date(state.endDate).getDate();
  // [1, 2, 3, ..., 31]
  const monthDays = new Array(31).fill(0).map((_v, i) => i + 1);

  // same as monthDats but mapped for table head
  const monthDaysHead = monthDays.map(i => ({
    Cell: EditableCell,
    Header: i.toString(),
    accessor: i.toString(),
    width: 35,
    className: 'cell',
    disabled: i > maxDay,
  }));

  let data = Object.values(state.workHours).map((wh, idx) => {
    const res: Record<string, any> = {
      id: idx,
      hourTypeId: wh.hourTypeId,
      itemNo: wh.hourTypeNo,
      hourName: wh.hourTypeCode,
      isBase: wh.isBase,
    };
    monthDays.forEach(d => {
      const day = wh.worksByDay.find(h => h.day === d);
      const color =
        workDays[d] &&
        (workDays[d].dayTypeCode === null ? '#eeeeef' : workDays[d].color);
      res[d] = {
        hours: day ? (day.hours === 0 ? '' : day.hours.toString()) : '',
        style: {
          background: color,
          color: Color(color).isDark() ? 'white' : 'black',
        },
      };
    });
    res.total = wh.worksByDay
      .reduce(
        (r, { day, hours }) => r + (!workDays[day].isAbsence ? hours : 0),
        0
      )
      .toFixed(2);
    return res;
  });

  // totals calculation (sum in bottom)
  const totals: Array<Record<string, any>> = [
    {
      hourName: intl.formatMessage(messages.totalBase),
    },
    {
      hourName: intl.formatMessage(messages.totalPresence),
    },
    {
      hourName: intl.formatMessage(messages.totalAbsence),
    },
    {
      hourName: intl.formatMessage(messages.totalAll),
    },
  ];

  monthDays.forEach(d => {
    if (d > maxDay) {
      return;
    }
    const wDays: any = Object.values(state.workHours).map(wh => ({
      ...wh,
      ...wh.worksByDay.reduce((res: any, cur) => {
        res[cur.day] = cur.hours;
        return res;
      }, {}),
    }));
    totals[0][d] = wDays.reduce(
      (res: number, row: any) =>
        res + (row.isBase === true && row[d] ? +row[d] : 0),
      0
    );
    totals[1][d] = wDays.reduce(
      (res: number, row: any) =>
        res + (!workDays[d].isAbsence && row[d] ? +row[d] : 0),
      0
    );
    totals[2][d] = wDays.reduce(
      (res: number, row: any) =>
        res + (workDays[d].isAbsence && row[d] ? +row[d] : 0),
      0
    );
    totals[3][d] = wDays.reduce(
      (res: number, row: any) => res + (row[d] ? +row[d] : 0),
      0
    );
  });
  data = data.concat(totals);

  const columns = [
    {
      Header: intl.formatMessage(messages.hourNumber),
      accessor: 'itemNo',
      width: 30,
    },
    {
      Header: intl.formatMessage(messages.hourType),
      accessor: 'hourName',
      width: 161,
    },
    {
      Header: intl.formatMessage(messages.totalAll),
      accessor: 'total',
      width: 70,
    },
    ...monthDaysHead,
  ];

  return { data, columns, ...factHoursDays(state.workHours, state.workDays) };
}

export function mapDaysTable(
  state: WorkTableData,
  year: number,
  month: number,
  intl: IntlShape
) {
  const data: Array<Record<string, any>> = [{ id: 0 }];
  state.workDays.forEach(wd => {
    const cellKey = new Date(year, month, wd.day).getUTCDay();
    if (cellKey === 0) {
      data.push({ id: data.length });
    } // create new row
    const background = wd.dayTypeCode === null ? '#eeeeef' : wd.color;
    data[data.length - 1][cellKey] = {
      dayNumber: wd.day,
      dayCode: wd.dayTypeCode,
      style: {
        background,
        color: Color(background).isDark() ? 'white' : 'black',
      },
    };
  });

  while (data.length < 6) {
    data.push({ id: data.length });
  }

  const columns = intl
    .formatMessage(messages.workDays)
    .split('_')
    .map((name, idx) => ({
      Header: name,
      Cell: WorkDaysCell,
      accessor: idx.toString(),
    }));

  return { columns, data, ...factHoursDays(state.workHours, state.workDays) };
}
